import type { SoilTextureScheme } from "../types/api";

// Equilateral triangle in barycentric (sand, silt, clay) space, projected to
// 2D: sand corner at bottom-right, silt corner at bottom-left, clay corner at
// the top. silt = 100 - sand - clay (implicit). Viewbox is in barycentric-%
// units so a single polygon definition reads as the vertex coords directly.

const H = Math.sqrt(3) / 2;
const TRI_H = 100 * H; // ~86.603

function toXY(sand: number, clay: number): [number, number] {
  return [sand + clay / 2, TRI_H - clay * H];
}

function pathFor(verts: [number, number][]): string {
  return verts
    .map(([s, c], i) => {
      const [x, y] = toXY(s, c);
      return `${i === 0 ? "M" : "L"} ${x.toFixed(3)} ${y.toFixed(3)}`;
    })
    .join(" ") + " Z";
}

// Distinct fill per class, derived from the integer class_id via the
// golden-ratio hue trick so adjacent IDs are far apart on the wheel.
function classColor(id: number, alpha: number): string {
  const hue = Math.round((id * 137.508) % 360);
  return `hsl(${hue}deg 60% 72% / ${alpha})`;
}

function centroid(verts: [number, number][]): [number, number] {
  // Drop the closing duplicate so the mean isn't biased toward the first vertex.
  const last = verts[verts.length - 1];
  const first = verts[0];
  const pts =
    last[0] === first[0] && last[1] === first[1] ? verts.slice(0, -1) : verts;
  let sx = 0;
  let sy = 0;
  for (const [s, c] of pts) {
    sx += s;
    sy += c;
  }
  return [sx / pts.length, sy / pts.length];
}

type Props = {
  scheme: SoilTextureScheme;
  /** (sand%, clay%) sample — null hides the marker. */
  sample: { sand: number; clay: number } | null;
  /** Class to highlight (the one `sample` falls in). */
  highlightedClassId: number | null;
  /** Optional caption shown below the triangle. */
  caption?: string;
};

export function SoilTextureTriangle({
  scheme,
  sample,
  highlightedClassId,
  caption,
}: Props) {
  const sampleXY = sample ? toXY(sample.sand, sample.clay) : null;

  // Padding gives the corner labels room to breathe.
  const padX = 14;
  const padTop = 10;
  const padBottom = 14;
  const vb = `${-padX} ${-padTop} ${100 + 2 * padX} ${TRI_H + padTop + padBottom}`;

  // Gridlines every 20% for all three constituents. Each line maps a
  // barycentric constant (sand/silt/clay = const) to its two endpoints on
  // the triangle's edges, then projected via toXY.
  const gridTicks = [20, 40, 60, 80];
  const gridLines = gridTicks.flatMap((t) => [
    // iso-sand: (sand=t, clay varies 0..100-t)
    { key: `s${t}`, a: toXY(t, 0), b: toXY(t, 100 - t) },
    // iso-clay: (clay=t, sand varies 0..100-t)
    { key: `c${t}`, a: toXY(0, t), b: toXY(100 - t, t) },
    // iso-silt: (silt=t → sand+clay=100-t; sand varies 0..100-t)
    { key: `l${t}`, a: toXY(100 - t, 0), b: toXY(0, 100 - t) },
  ]);

  return (
    <div className="flex flex-col">
      <svg
        viewBox={vb}
        className="w-full h-auto"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* class polygons */}
        {scheme.classes.map((c) => {
          const isHighlight = c.class_id === highlightedClassId;
          return (
            <path
              key={c.class_id}
              d={pathFor(c.vertices)}
              fill={classColor(c.class_id, isHighlight ? 0.85 : 0.32)}
              stroke={isHighlight ? "rgba(0,0,0,0.7)" : "rgba(0,0,0,0.18)"}
              strokeWidth={isHighlight ? 0.55 : 0.28}
              strokeLinejoin="round"
            >
              <title>
                {c.class_name} (id {c.class_id})
              </title>
            </path>
          );
        })}

        {/* gridlines on top of fills, under the outer border */}
        <g stroke="rgba(0,0,0,0.10)" strokeWidth={0.18}>
          {gridLines.map((l) => (
            <line
              key={l.key}
              x1={l.a[0]}
              y1={l.a[1]}
              x2={l.b[0]}
              y2={l.b[1]}
            />
          ))}
        </g>

        {/* outer triangle */}
        <path
          d={`M 0 ${TRI_H} L 100 ${TRI_H} L 50 0 Z`}
          fill="none"
          stroke="rgba(0,0,0,0.7)"
          strokeWidth={0.55}
          strokeLinejoin="round"
        />

        {/* highlighted polygon's name inside the triangle */}
        {highlightedClassId != null &&
          (() => {
            const c = scheme.classes.find(
              (k) => k.class_id === highlightedClassId,
            );
            if (!c) return null;
            const [sx, sy] = centroid(c.vertices);
            const [x, y] = toXY(sx, sy);
            return (
              <text
                x={x}
                y={y}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={3.4}
                fontWeight={600}
                fill="rgba(0,0,0,0.85)"
                pointerEvents="none"
                style={{ paintOrder: "stroke" }}
                stroke="white"
                strokeWidth={0.6}
              >
                {c.class_name}
              </text>
            );
          })()}

        {/* corner labels */}
        <text
          x={0}
          y={TRI_H + 5.5}
          textAnchor="middle"
          fontSize={4}
          fill="rgba(0,0,0,0.65)"
        >
          silt
        </text>
        <text
          x={100}
          y={TRI_H + 5.5}
          textAnchor="middle"
          fontSize={4}
          fill="rgba(0,0,0,0.65)"
        >
          sand
        </text>
        <text
          x={50}
          y={-3}
          textAnchor="middle"
          fontSize={4}
          fill="rgba(0,0,0,0.65)"
        >
          clay
        </text>

        {/* sample marker */}
        {sampleXY && (
          <g>
            <circle
              cx={sampleXY[0]}
              cy={sampleXY[1]}
              r={2.2}
              fill="white"
              stroke="rgba(0,0,0,0.9)"
              strokeWidth={0.7}
            />
            <circle
              cx={sampleXY[0]}
              cy={sampleXY[1]}
              r={0.9}
              fill="rgba(0,0,0,0.9)"
            />
          </g>
        )}
      </svg>

      {caption && (
        <div className="mt-1 text-center text-[10px] uppercase tracking-[0.04em] text-black/40">
          {caption}
        </div>
      )}
    </div>
  );
}
